import { useState, useEffect, FC, useRef, WheelEvent } from "react";
import mermaid from "mermaid";
import { ZoomIn, ZoomOut, RotateCcw, Sun, Moon, Maximize, Minimize, GitBranch } from "lucide-react";

interface MermaidViewerProps {
  diagram: string;
  isDemo?: boolean;
  onServiceClick?: (serviceUrl: string) => void; // New prop for handling service clicks
}

const MAX_HEIGHT = 900;
const MIN_HEIGHT = 900;
const MIN_ZOOM = 0.1;  // 10% zoom out (more flexible)
const MAX_ZOOM = 20;   // 2000% zoom in (unlimited practically)
const ZOOM_STEP = 0.2; // 20% per zoom click

// Define available curve styles
const CURVE_STYLES = ["linear", "stepBefore", "stepAfter", "basis"] as const;
type CurveStyle = typeof CURVE_STYLES[number];

// Light theme configuration
const lightThemeConfig = {
  theme: "default",
  themeVariables: {},
  themeCSS: `
  
    `
};

// Dark theme configuration
const darkThemeConfig = {
  theme: "dark",
  themeVariables: {},
  themeCSS: `
              g.label span.nodeLabel {
                color: #2c3e50 !important;
              }
              g.label foreignObject div span.nodeLabel {
                color: #2c3e50 !important;
              }
            `
};

const MermaidViewer: FC<MermaidViewerProps> = ({ diagram, onServiceClick }) => {
  const [loading, setLoading] = useState(true);
  const [svg, setSvg] = useState("");
  const [zoom, setZoom] = useState(1);
  const [uniqueId] = useState(`mermaid-${Math.random().toString(36).substr(2, 9)}`);
  const [isPanning, setIsPanning] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [startPanPosition, setStartPanPosition] = useState({ x: 0, y: 0 });
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [curveStyle, setCurveStyle] = useState<CurveStyle>("linear");
  const [isHovered, setIsHovered] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const svgContainerRef = useRef<HTMLDivElement>(null);
  
  // Function to add hover effects to arrows/edges
  const addArrowHoverEffects = () => {
    if (!svgContainerRef.current) return;
    
    const svgElement = svgContainerRef.current.querySelector("svg");
    if (!svgElement) return;
    
    console.log("Adding hover effects to SVG:", svgElement);
    
    // Find all edge paths - try multiple selectors
    const edgePathSelectors = [
      ".edgePaths path",
      ".edgePath path",
      "path[class*=\"edge\"]",
      "path[id*=\"L-\"]"
    ];
    
    let edgePaths: NodeListOf<Element> | null = null;
    
    for (const selector of edgePathSelectors) {
      const foundPaths = svgElement.querySelectorAll(selector);
      if (foundPaths.length > 0) {
        console.log(`Found ${foundPaths.length} paths with selector: ${selector}`);
        edgePaths = foundPaths;
        break;
      }
    }
    
    if (!edgePaths || edgePaths.length === 0) {
      console.log("No edge paths found, trying to find all paths...");
      edgePaths = svgElement.querySelectorAll("path");
      console.log(`Found ${edgePaths.length} total paths`);
    }
    
    // Find edge labels
    const edgeLabels = svgElement.querySelectorAll(".edgeLabel");
    console.log(`Found ${edgeLabels.length} edge labels`);
    
    // Find arrowheads in markers
    const arrowheads = svgElement.querySelectorAll("defs marker[id*=\"pointEnd\"] path, defs marker[id*=\"arrowhead\"] path");
    console.log(`Found ${arrowheads.length} arrowheads`);
    const hoverWidth = "4px";
    
    // Function to reset path style
    const resetPathStyle = (pathEl: SVGPathElement, labelIndex?: number) => {
      const origStroke = pathEl.getAttribute("data-original-stroke") || (isDarkMode ? "#f8f8f2" : "#333333");
      const origWidth = pathEl.getAttribute("data-original-stroke-width") || "2px";
      
      pathEl.style.setProperty("stroke", origStroke, "important");
      pathEl.style.setProperty("stroke-width", origWidth, "important");
      pathEl.style.removeProperty("filter");
      
      if (typeof labelIndex === "number") {
        const correspondingLabel = edgeLabels[labelIndex] as SVGElement;
        if (correspondingLabel) {
          correspondingLabel.style.filter = "";
          const labelSpan = correspondingLabel.querySelector("span.edgeLabel");
          if (labelSpan) {
            (labelSpan as HTMLElement).style.removeProperty("color");
            (labelSpan as HTMLElement).style.removeProperty("font-weight");
          }
        }
        
        const correspondingArrowhead = arrowheads[labelIndex] as SVGPathElement;
        if (correspondingArrowhead) {
          correspondingArrowhead.style.removeProperty("fill");
          correspondingArrowhead.style.filter = "";
        }
      }
    };
    
    // Function to reset all highlighted paths
    const resetAllHighlightedPaths = () => {
      // Get current highlighted paths from DOM instead of state
      const currentlyHighlighted = svgElement.querySelectorAll("path[data-is-highlighted=\"true\"]");
      currentlyHighlighted.forEach((path) => {
        const pathEl = path as SVGPathElement;
        const index = parseInt(pathEl.getAttribute("data-path-index") || "0");
        resetPathStyle(pathEl, index);
        pathEl.removeAttribute("data-is-highlighted");
      });
    };
    
    Array.from(edgePaths).forEach((path, index) => {
      const pathElement = path as SVGPathElement;
      
      if (pathElement.getAttribute("data-hover-added")) return;
      
      console.log(`Processing path ${index}:`, pathElement);
      
      const originalStroke = pathElement.getAttribute("stroke") ||
        window.getComputedStyle(pathElement).stroke ||
        (isDarkMode ? "#f8f8f2" : "#333333");
      const originalStrokeWidth = pathElement.getAttribute("stroke-width") ||
        window.getComputedStyle(pathElement).strokeWidth ||
        "2px";
      
      pathElement.setAttribute("data-original-stroke", originalStroke);
      pathElement.setAttribute("data-original-stroke-width", originalStrokeWidth);
      pathElement.setAttribute("data-path-index", index.toString());
      
      // Create hover zone
      const hoverZone = pathElement.cloneNode(true) as SVGPathElement;
      hoverZone.setAttribute("stroke-width", "12px");
      hoverZone.setAttribute("stroke", "transparent");
      hoverZone.setAttribute("fill", "none");
      hoverZone.setAttribute("pointer-events", "stroke");
      hoverZone.setAttribute("data-hover-zone", "true");
      hoverZone.setAttribute("data-path-index", index.toString());
      //hoverZone.style.cursor = 'default';
      
      hoverZone.removeAttribute("data-hover-added");
      hoverZone.removeAttribute("data-original-stroke");
      hoverZone.removeAttribute("data-original-stroke-width");
      
      pathElement.parentNode?.insertBefore(hoverZone, pathElement.nextSibling);
      
      const handleMouseEnter = () => {
        console.log("Mouse enter on path:", pathElement);
        
        // Reset all previously highlighted paths
        resetAllHighlightedPaths();
        
        // Mark this path as highlighted
        pathElement.setAttribute("data-is-highlighted", "true");
        
        // Apply hover styles - SET THE RED COLOR FOR THE PATH
        pathElement.style.setProperty("stroke", "#e74c3c", "important"); // RED COLOR
        pathElement.style.setProperty("stroke-width", hoverWidth, "important");
        pathElement.style.setProperty("filter", "drop-shadow(0 0 6px rgba(231, 76, 60, 0.6))", "important"); // RED GLOW
        
        // Highlight corresponding label
        const correspondingLabel = edgeLabels[index] as SVGElement;
        if (correspondingLabel) {
          correspondingLabel.style.filter = "drop-shadow(0 0 4px rgba(231, 76, 60, 0.6))"; // RED GLOW
          const labelSpan = correspondingLabel.querySelector("span.edgeLabel");
          if (labelSpan) {
            (labelSpan as HTMLElement).style.setProperty("color", "#e74c3c", "important"); // RED TEXT
            (labelSpan as HTMLElement).style.setProperty("font-weight", "bold", "important");
          }
        }
        
        // Highlight arrowhead
        const correspondingArrowhead = arrowheads[index] as SVGPathElement;
        if (correspondingArrowhead) {
          correspondingArrowhead.style.setProperty("fill", "#e74c3c", "important"); // RED ARROWHEAD
          correspondingArrowhead.style.filter = "drop-shadow(0 0 3px rgba(231, 76, 60, 0.6))"; // RED GLOW
        }
      };
      
      [pathElement, hoverZone].forEach(element => {
        element.addEventListener("mouseenter", handleMouseEnter);
      });
      
      pathElement.setAttribute("data-hover-added", "true");
      hoverZone.setAttribute("data-hover-added", "true");
      
      console.log(`Added hover effects to path ${index}`);
    });
    
    // Add container mouse leave to reset all highlights when leaving the SVG area
    const handleContainerMouseLeave = () => {
      resetAllHighlightedPaths();
    };
    
    svgElement.addEventListener("mouseleave", handleContainerMouseLeave);
    
    // Add hover effects to edge labels
    Array.from(edgeLabels).forEach((label, index) => {
      const labelElement = label as SVGElement;
      
      if (labelElement.getAttribute("data-hover-added")) return;
      
      const correspondingPath = edgePaths![index] as SVGPathElement;
      
      const handleLabelMouseEnter = () => {
        if (correspondingPath) {
          correspondingPath.dispatchEvent(new Event("mouseenter"));
        }
      };
      
      labelElement.addEventListener("mouseenter", handleLabelMouseEnter);
      //labelElement.style.cursor = 'default';
      labelElement.setAttribute("data-hover-added", "true");
    });
    
    console.log(`Successfully added hover effects to ${edgePaths.length} paths and ${edgeLabels.length} labels`);
  };
  // Function to clean up hover effects
  const cleanupHoverEffects = () => {
    if (!svgContainerRef.current) return;
    
    const svgElement = svgContainerRef.current.querySelector("svg");
    if (!svgElement) return;
    
    // Clean up paths
    const pathsWithHover = svgElement.querySelectorAll("path[data-hover-added=\"true\"]");
    pathsWithHover.forEach(path => {
      const pathElement = path as SVGPathElement;
      
      // Create new element to remove all event listeners
      const newPath = pathElement.cloneNode(true) as SVGPathElement;
      newPath.removeAttribute("data-hover-added");
      newPath.removeAttribute("data-original-stroke");
      newPath.removeAttribute("data-original-stroke-width");
      // Clear any inline styles
      newPath.style.cssText = "";
      
      pathElement.parentNode?.replaceChild(newPath, pathElement);
    });
    
    // Clean up labels
    const labelsWithHover = svgElement.querySelectorAll(".edgeLabel[data-hover-added=\"true\"]");
    labelsWithHover.forEach(label => {
      const labelElement = label as SVGElement;
      
      const newLabel = labelElement.cloneNode(true) as SVGElement;
      newLabel.removeAttribute("data-hover-added");
      newLabel.style.cursor = "default";
      
      labelElement.parentNode?.replaceChild(newLabel, labelElement);
    });
  };
  
  const renderDiagram = async () => {
    try {
      setLoading(true);
      const currentTheme = isDarkMode ? darkThemeConfig : lightThemeConfig;
      
      mermaid.initialize({
        startOnLoad: true,
        securityLevel: "loose",
        maxTextSize: 99999999,
        fontFamily: "Fira Code",
        ...currentTheme,
        flowchart: {
          useMaxWidth: true,
          htmlLabels: true,
          curve: curveStyle,
          nodeSpacing: 120,
          rankSpacing: 150,
          padding: 50,
          diagramPadding: 50
        },
        sequence: {
          useMaxWidth: true,
          showSequenceNumbers: true
        },
        themeVariables: {
          ...currentTheme.themeVariables,
          primaryColor: isDarkMode ? "#44475a" : "#f0f4ff",
          primaryTextColor: isDarkMode ? "#f8f8f2" : "#333",
          primaryBorderColor: isDarkMode ? "#bd93f9" : "#4b72b0",
          lineColor: isDarkMode ? "#f8f8f2" : "#333333",
          // Node sizing variables
          nodeBkg: isDarkMode ? "#44475a" : "#f0f4ff",
          nodeTextColor: isDarkMode ? "#f8f8f2" : "#333",
          // Force larger node dimensions
          cScale0: "#ff0000",
          cScale1: "#ff0000",
          cScale2: "#ff0000"
        },
        
        themeCSS: `
  ${currentTheme.themeCSS || ""}

  /* Keyframes for glowing animation */
  @keyframes glow {
    0% {
      box-shadow: 0 0 5px ${isDarkMode ? "#bd93f9" : "#e2e8f0"}, 0 0 10px ${isDarkMode ? "#bd93f9" : "#e2e8f0"}, 0 0 15px ${isDarkMode ? "#bd93f9" : "#e2e8f0"};
    }
    50% {
      box-shadow: 0 0 8px ${isDarkMode ? "#a78bfa" : "#8b5cf6"}, 0 0 16px ${isDarkMode ? "#a78bfa" : "#8b5cf6"}, 0 0 24px ${isDarkMode ? "#a78bfa" : "#8b5cf6"};
    }
    100% {
      box-shadow: 0 0 5px ${isDarkMode ? "#bd93f9" : "#e2e8f0"}, 0 0 10px ${isDarkMode ? "#bd93f9" : "#e2e8f0"}, 0 0 15px ${isDarkMode ? "#bd93f9" : "#e2e8f0"};
    }
  }

  @keyframes pulse {
    0% {
      filter: drop-shadow(0 0 3px ${isDarkMode ? "rgba(189, 147, 249, 0.5)" : "rgba(139, 92, 246, 0.4)"});
    }
    50% {
      filter: drop-shadow(0 0 8px ${isDarkMode ? "rgba(167, 139, 250, 0.7)" : "rgba(139, 92, 246, 0.6)"});
    }
    100% {
      filter: drop-shadow(0 0 3px ${isDarkMode ? "rgba(189, 147, 249, 0.5)" : "rgba(139, 92, 246, 0.4)"});
    }
  }

  /* Style clickable nodes */
  a[data-node="true"] .flowchart-label rect,
  a.clickable .flowchart-label rect,
  .clickable rect.basic.label-container {
    stroke: ${isDarkMode ? "#a78bfa" : "#8b5cf6"} !important;
    stroke-width: 2.5px !important;
    fill: ${isDarkMode ? "#44475a" : "#faf5ff"} !important;
    animation: pulse 2.5s ease-in-out infinite !important;
    cursor: pointer !important;
  }

  /* Style clickable node text */
  a[data-node="true"] .nodeLabel,
  a.clickable .nodeLabel,
  .clickable .nodeLabel {
    color: ${isDarkMode ? "#a78bfa" : "#7c3aed"} !important;
    font-weight: 600 !important;
    cursor: pointer !important;
  }

  /* Add subtle background glow for better visibility */
  a[data-node="true"],
  a.clickable {
    filter: drop-shadow(0 0 6px ${isDarkMode ? "rgba(167, 139, 250, 0.25)" : "rgba(139, 92, 246, 0.2)"});
  }

  /* Hover effect for clickable nodes */
  a[data-node="true"]:hover .flowchart-label rect,
  a.clickable:hover .flowchart-label rect,
  .clickable:hover rect.basic.label-container {
    stroke: ${isDarkMode ? "#c4b5fd" : "#a855f7"} !important;
    stroke-width: 3px !important;
    filter: drop-shadow(0 0 12px ${isDarkMode ? "rgba(196, 181, 253, 0.6)" : "rgba(168, 85, 247, 0.5)"}) !important;
    animation: none !important;
  }

  /* Hover effect for clickable node text */
  a[data-node="true"]:hover .nodeLabel,
  a.clickable:hover .nodeLabel,
  .clickable:hover .nodeLabel {
    color: ${isDarkMode ? "#c4b5fd" : "#9333ea"} !important;
  }

  a[data-node="true"]:hover,
  a.clickable:hover {
    filter: drop-shadow(0 0 10px ${isDarkMode ? "rgba(196, 181, 253, 0.4)" : "rgba(168, 85, 247, 0.35)"});
  }



                /* Only target rectangle nodes specifically, not database shapes */
  g.flowchart-node rect.basic.label-container,
  g.flowchart-node rect[class*="rect"],
  .node rect.basic.label-container {
    min-width: 180px !important;
    min-height: 70px !important;
    fill: ${isDarkMode ? "#44475a" : "#f0f4ff"} !important;
    stroke: ${isDarkMode ? "#bd93f9" : "#4b72b0"} !important;
    stroke-width: 2px !important;
    rx: 6 !important;
    ry: 6 !important;
  }

  /* Adjust text size to fit nodes better */
  .nodeLabel,
  span.nodeLabel,
  .flowchart-label {
    font-size: 32px !important;
    font-weight: 600 !important;
    color: ${isDarkMode ? "#f8f8f2" : "#333"} !important;
    line-height: 1.2 !important;
    text-anchor: middle !important;
    dominant-baseline: central !important;
    white-space: pre-wrap !important;
    word-wrap: break-word !important;
  }

  /* Adjust foreignObject for text containers */
  foreignObject {
    width: 270px !important;
    height: 60px !important;
    overflow: visible !important;
  }

  foreignObject div {
    width: 100% !important;
    height: 100% !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    padding: 8px !important;
    box-sizing: border-box !important;
    text-align: center !important;
    overflow: visible !important;
  }

  /* Ensure database shapes keep their original styling */
  g.flowchart-node path[class*="database"],
  .node path[class*="database"],
  path.flowchart-database {
    /* Keep original database shape styling */
  }

  /* Remove all stroke styles from paths to allow JS control */
  .edgePaths path, .edgePath path, .flowchart-link {
    stroke: none !important;
    stroke-width: none !important;
    transition: stroke 0.2s ease, stroke-width 0.2s ease, filter 0.2s ease !important;
  }

  .edgeLabel {
    transition: filter 0.2s ease !important;
    font-size: 32px !important;
    font-weight: normal !important;
  }

  .edgeLabel span {
    transition: color 0.2s ease, font-weight 0.2s ease !important;
  }
`
      });
      
      
      const { svg } = await mermaid.render(uniqueId, diagram);
      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = svg;
      const svgElement = tempDiv.querySelector("svg");
      
      if (svgElement) {
        // Remove the generated <style> tag that contains stroke rules
        const styleTag = svgElement.querySelector("style");
        if (styleTag) {
          let cssText = styleTag.textContent || "";
          // Only remove specific stroke rules for paths, keep everything else
          cssText = cssText.replace(/#[^}]*\.edgePath\s+\.path[^}]*stroke[^}]*}/g, "");
          cssText = cssText.replace(/#[^}]*\.flowchart-link[^}]*stroke[^}]*}/g, "");
          cssText = cssText.replace(/#[^}]*\.arrowheadPath[^}]*}/g, "");
          styleTag.textContent = cssText;
        }
        
        
        // Remove stroke attributes from all path elements
        const allPaths = svgElement.querySelectorAll("path");
        allPaths.forEach(path => {
          path.removeAttribute("stroke");
          path.removeAttribute("stroke-width");
          path.removeAttribute("stroke-dasharray");
          // Set default stroke via JS to ensure control
          if (path.getAttribute("class")?.includes("flowchart-link") ||
            path.getAttribute("id")?.startsWith("L-")) {
            path.style.stroke = isDarkMode ? "#f8f8f2" : "#333333";
            path.style.strokeWidth = "2px";
            path.style.fill = "none";
          }
        });
        
        const viewBox = svgElement.getAttribute("viewBox")?.split(" ").map(Number) || [0, 0, 0, 0];
        const [, , width, height] = viewBox;
        const aspectRatio = width / height;
        const newHeight = Math.min(MAX_HEIGHT, height);
        const newWidth = newHeight * aspectRatio;
        
        svgElement.setAttribute("width", `${newWidth}`);
        svgElement.setAttribute("height", `${newHeight}`);
        svgElement.style.maxWidth = "100%";
        
        setSvg(tempDiv.innerHTML);
      } else {
        setSvg(svg);
      }
    } catch (error) {
      // console.error('Failed to render diagram:', error);
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    renderDiagram();
  }, [diagram, uniqueId, isDarkMode, curveStyle]);
  
  // Add hover effects after SVG is updated
  useEffect(() => {
    if (svg && !loading) {
      // Use setTimeout to ensure DOM is updated
      const timer = setTimeout(() => {
        console.log("Adding hover effects...");
        cleanupHoverEffects(); // Clean up previous effects
        addArrowHoverEffects(); // Add new effects
      }, 200); // Increased timeout

      return () => clearTimeout(timer);
    }
  }, [svg, loading, isDarkMode]);

  // Add click handler for service links
  useEffect(() => {
    if (!onServiceClick || !svg) return;

    const svgContainer = svgContainerRef.current;
    if (!svgContainer) return;

    // Wait for SVG to be fully rendered in DOM
    const timer = setTimeout(() => {
      const handleClick = (e: Event) => {
        const mouseEvent = e as MouseEvent;
        const target = mouseEvent.target as HTMLElement;

        // Find the closest link element
        const link = target.closest('a') ||
                     target.closest('[data-node="true"]') ||
                     (target.tagName === 'A' ? target : null);

        if (link) {
          const href = link.getAttribute('href') || link.getAttribute('xlink:href');
          console.log('Diagram click detected:', href);

          if (href && href.startsWith('/')) {
            mouseEvent.preventDefault();
            mouseEvent.stopPropagation();
            mouseEvent.stopImmediatePropagation();
            console.log('Calling onServiceClick with:', href);
            onServiceClick(href);
            return false;
          }
        }
      };

      // Add listener to the actual SVG element
      const svgElement = svgContainer.querySelector('svg');
      if (svgElement) {
        // Remove any existing click handlers first
        const allLinks = svgElement.querySelectorAll('a[href], a[xlink\\:href]');
        allLinks.forEach((link) => {
          link.addEventListener('click', handleClick, { capture: true });
        });

        svgElement.addEventListener('click', handleClick, { capture: true });
      }
    }, 300);

    return () => {
      clearTimeout(timer);
    };
  }, [svg, onServiceClick]);

  // Add global wheel event listener to prevent browser zoom when hovering over component
  useEffect(() => {
    const handleGlobalWheel = (e: WheelEvent) => {
      if (!containerRef.current || !isHovered) return;
      
      // Check if the event target is within our container
      const target = e.target as Element;
      if (containerRef.current.contains(target)) {
        // Prevent default to stop browser zoom, but don't stop our custom zoom
        e.preventDefault();
        e.stopPropagation();
        
        // Handle our custom zoom here as well
        let delta = 0;
        
        if (e.ctrlKey || e.metaKey) {
          // Handle pinch-to-zoom (trackpad) or Ctrl+scroll
          delta = e.deltaY * -0.01;
        } else {
          // Handle regular scroll wheel
          delta = e.deltaY * -0.005;
        }
        
        handleZoom(delta);
      }
    };
    
    // Add listener with passive: false to allow preventDefault
    document.addEventListener("wheel", handleGlobalWheel as any, {
      passive: false,
      capture: true
    });
    
    return () => {
      document.removeEventListener("wheel", handleGlobalWheel as any, {
        capture: true
      });
    };
  }, [isHovered, zoom, position]); // Add zoom and position as dependencies
  
  // Add keyboard event handler for zoom controls
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Only handle keyboard events when the container is focused or hovered
      if (!containerRef.current) return;
      
      // Check if the container is focused or if we're hovering over it
      const isContainerFocused = document.activeElement === containerRef.current;
      const shouldHandleKeyboard = isHovered || isContainerFocused;
      
      if (!shouldHandleKeyboard) return;
      
      // Prevent default browser zoom behavior
      if ((e.ctrlKey || e.metaKey) && (e.key === "=" || e.key === "+" || e.key === "-" || e.key === "0")) {
        e.preventDefault();
        e.stopPropagation();
        
        if (e.key === "=" || e.key === "+") {
          handleZoomIn();
        } else if (e.key === "-") {
          handleZoomOut();
        } else if (e.key === "0") {
          resetView();
        }
      }
    };
    
    document.addEventListener("keydown", handleKeyDown, { capture: true });
    return () => {
      document.removeEventListener("keydown", handleKeyDown, { capture: true });
    };
  }, [zoom, position, isHovered]);
  
  // Function to cycle through arrow styles
  const toggleCurveStyle = () => {
    const currentIndex = CURVE_STYLES.indexOf(curveStyle);
    const nextIndex = (currentIndex + 1) % CURVE_STYLES.length;
    setCurveStyle(CURVE_STYLES[nextIndex]);
  };
  
  // Add fullscreen toggle function
  const toggleFullscreen = () => {
    if (!containerRef.current) return;
    
    if (!isFullscreen) {
      if (containerRef.current.requestFullscreen) {
        containerRef.current.requestFullscreen();
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      }
    }
  };
  
  // Listen for fullscreen change events and ESC key
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && isFullscreen) {
        toggleFullscreen();
      }
    };
    
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    document.addEventListener("keydown", handleKeyDown);
    
    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isFullscreen]);
  
  const resetView = () => {
    setZoom(1);
    setPosition({ x: 0, y: 0 });
  };
  
  const handleThemeToggle = () => {
    setIsDarkMode(!isDarkMode);
  };
  
  const handleZoom = (delta: number) => {
    if (!containerRef.current || !svgContainerRef.current) return;
    const newZoom = Math.min(Math.max(zoom + delta, MIN_ZOOM), MAX_ZOOM);
    const scaleFactor = newZoom / zoom;
    const newX = position.x * scaleFactor;
    const newY = position.y * scaleFactor;
    setZoom(newZoom);
    setPosition({ x: newX, y: newY });
  };
  
  const handleZoomIn = () => handleZoom(ZOOM_STEP);
  const handleZoomOut = () => handleZoom(-ZOOM_STEP);
  
  // Simplified wheel event handler - the global handler will take care of prevention
  const handleWheel = (e: WheelEvent<HTMLDivElement>) => {
    // This will be handled by the global listener, but keep it as backup
    e.preventDefault();
    e.stopPropagation();
    
    let delta = 0;
    
    if (e.ctrlKey || e.metaKey) {
      delta = e.deltaY * -0.01;
    } else {
      delta = e.deltaY * -0.005;
    }
    
    handleZoom(delta);
  };
  
  const handleMouseDown = (e: React.MouseEvent) => {
    // Prevent default to avoid any browser zoom behaviors
    e.preventDefault();
    setIsPanning(true);
    setStartPanPosition({
      x: e.clientX - position.x,
      y: e.clientY - position.y
    });
  };
  
  const handleMouseMove = (e: React.MouseEvent) => {
    if (isPanning) {
      const newX = e.clientX - startPanPosition.x;
      const newY = e.clientY - startPanPosition.y;
      setPosition({ x: newX, y: newY });
    }
  };
  
  const handleMouseUp = () => {
    setIsPanning(false);
  };
  
  // Handle mouse enter/leave for hover state
  const handleMouseEnter = () => {
    setIsHovered(true);
  };
  
  const handleMouseLeave = () => {
    setIsHovered(false);
    setIsPanning(false);
  };
  
  // Handle touch events for mobile pinch-to-zoom
  const [initialPinchDistance, setInitialPinchDistance] = useState<number | null>(null);
  const [initialZoom, setInitialZoom] = useState(1);
  
  const getTouchDistance = (touch1: React.Touch, touch2: React.Touch): number => {
    const dx = touch1.clientX - touch2.clientX;
    const dy = touch1.clientY - touch2.clientY;
    return Math.sqrt(dx * dx + dy * dy);
  };
  
  const handleTouchStart = (e: React.TouchEvent) => {
    if (e.touches.length === 2) {
      // Start pinch gesture
      const distance = getTouchDistance(e.touches[0], e.touches[1]);
      setInitialPinchDistance(distance);
      setInitialZoom(zoom);
      e.preventDefault();
    }
  };
  
  const handleTouchMove = (e: React.TouchEvent) => {
    if (e.touches.length === 2 && initialPinchDistance) {
      // Continue pinch gesture
      const currentDistance = getTouchDistance(e.touches[0], e.touches[1]);
      const scale = currentDistance / initialPinchDistance;
      const newZoom = Math.min(Math.max(initialZoom * scale, MIN_ZOOM), MAX_ZOOM);
      setZoom(newZoom);
      e.preventDefault();
    }
  };
  
  const handleTouchEnd = (e: React.TouchEvent) => {
    if (e.touches.length < 2) {
      setInitialPinchDistance(null);
    }
  };
  
  // Handle gesturestart and gesturechange for Safari
  useEffect(() => {
    const handleGesture = (e: Event) => {
      if (!containerRef.current || !isHovered) return;
      
      const target = e.target as Element;
      if (containerRef.current.contains(target)) {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    
    document.addEventListener("gesturestart", handleGesture, { passive: false });
    document.addEventListener("gesturechange", handleGesture, { passive: false });
    document.addEventListener("gestureend", handleGesture);
    
    return () => {
      document.removeEventListener("gesturestart", handleGesture);
      document.removeEventListener("gesturechange", handleGesture);
      document.removeEventListener("gestureend", handleGesture);
    };
  }, [isHovered]);
  
  // Cleanup hover effects on unmount
  useEffect(() => {
    return () => {
      cleanupHoverEffects();
    };
  }, []);
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64 w-full">
        <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  
  return (
    <div
      className={`relative w-full transition-colors duration-300 ${
        isDarkMode ? "bg-[#282a36]" : "bg-gray-50"
      }`}
      style={{
        maxHeight: isFullscreen ? "100vh" : `${MAX_HEIGHT}px`,
        minHeight: isFullscreen ? "100vh" : `${MIN_HEIGHT}px`,
        // Allow pinch-to-zoom but prevent other touch actions
        touchAction: "pan-x pan-y pinch-zoom",
        userSelect: "none",
        // Additional CSS to prevent zoom
        WebkitTouchCallout: "none",
        WebkitUserSelect: "none",
        KhtmlUserSelect: "none",
        MozUserSelect: "none",
        msUserSelect: "none"
      }}
    >
      {/* Fullscreen Close Button - Only visible in fullscreen */}
      {isFullscreen && (
        <div className="absolute top-4 left-4 z-20">
          <button
            onClick={toggleFullscreen}
            className="p-3 rounded-full bg-red-500 hover:bg-red-600 text-white shadow-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-red-400"
            aria-label="Exit fullscreen"
            title="Exit fullscreen (ESC)"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}
      
      <div className="absolute top-4 right-4 flex gap-2 bg-opacity-80 p-2 rounded-lg shadow-md z-10"
           style={{
             backgroundColor: isDarkMode ? "#44475a" : "#ffffff"
           }}>
        {/* Zoom level indicator */}
        <div className={`px-3 py-2 rounded-lg text-sm font-medium ${
          isDarkMode ? "text-[#f8f8f2] bg-[#282a36]" : "text-gray-700 bg-gray-100"
        }`}>
          {Math.round(zoom * 100)}%
        </div>
        
        {/* Fullscreen toggle button */}
        <button
          onClick={toggleFullscreen}
          className={`p-2 rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 ${
            isDarkMode
              ? "hover:bg-[#282a36] focus:ring-[#bd93f9] text-[#f8f8f2]"
              : "hover:bg-gray-100 focus:ring-blue-500 text-gray-700"
          }`}
          aria-label={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
        >
          {isFullscreen ? <Minimize className="w-5 h-5" /> : <Maximize className="w-5 h-5" />}
        </button>
        {/* Arrow style toggle button */}
        <button
          onClick={toggleCurveStyle}
          className={`p-2 rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 ${
            isDarkMode
              ? "hover:bg-[#282a36] focus:ring-[#bd93f9] text-[#f8f8f2]"
              : "hover:bg-gray-100 focus:ring-blue-500 text-gray-700"
          }`}
          aria-label={`Change arrow style (current: ${curveStyle})`}
          title={`Arrow style: ${curveStyle}`}
        >
          <GitBranch className="w-5 h-5" />
        </button>
        
        
        <button
          onClick={handleThemeToggle}
          className={`hidden p-2 rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 ${
            isDarkMode
              ? "hover:bg-[#282a36] focus:ring-[#bd93f9] text-[#f8f8f2]"
              : "hover:bg-gray-100 focus:ring-blue-500 text-gray-700"
          }`}
          aria-label={isDarkMode ? "Switch to light mode" : "Switch to dark mode"}
        >
          {isDarkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
        </button>
        <button
          onClick={handleZoomIn}
          className={`p-2 rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 ${
            isDarkMode
              ? "hover:bg-[#282a36] focus:ring-[#bd93f9] text-[#f8f8f2]"
              : "hover:bg-gray-100 focus:ring-blue-500 text-gray-700"
          }`}
          aria-label="Zoom in"
        >
          <ZoomIn className="w-5 h-5" />
        </button>
        <button
          onClick={handleZoomOut}
          className={`p-2 rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 ${
            isDarkMode
              ? "hover:bg-[#282a36] focus:ring-[#bd93f9] text-[#f8f8f2]"
              : "hover:bg-gray-100 focus:ring-blue-500 text-gray-700"
          }`}
          aria-label="Zoom out"
        >
          <ZoomOut className="w-5 h-5" />
        </button>
        <button
          onClick={resetView}
          className={`p-2 rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 ${
            isDarkMode
              ? "hover:bg-[#282a36] focus:ring-[#bd93f9] text-[#f8f8f2]"
              : "hover:bg-gray-100 focus:ring-blue-500 text-gray-700"
          }`}
          aria-label="Reset view"
        >
          <RotateCcw className="w-5 h-5" />
        </button>
      </div>
      
      {/* Enhanced Legend Panel - Made Narrower */}
      <div className="absolute right-4 bg-opacity-95 p-3 rounded-xl shadow-xl z-10 max-w-xs border"
           style={{
             backgroundColor: "#ffffff",
             borderColor: "#e2e8f0",
             top: "120px",
             width: "280px"
           }}>
        {/* Header */}
        <div className="flex items-center gap-2 mb-2">
          <span className="text-base">✨</span>
          <div className="text-sm font-bold text-[#7c3aed]">
            Ready to Build?
          </div>
        </div>
        
        {/* Main message - More compact */}
        <div className="text-xs leading-relaxed mb-2 text-gray-700">
          Click on the <span
          className="font-bold px-1 py-0.5 rounded text-[#7c3aed] bg-[#faf5ff]">glowing services</span> to
          explore implementation details.
        </div>
        
        {/* Visual indicators - More compact */}
        <div className="space-y-1.5">
          <div className="flex items-center gap-2">
            <div
              className="w-5 h-2.5 rounded border-2 animate-pulse flex-shrink-0"
              style={{
                borderColor: "#8b5cf6",
                backgroundColor: "rgba(139, 92, 246, 0.15)"
              }}
            ></div>
            <span className="text-xs text-gray-600">
                Interactive Service
            </span>
          </div>
          
          <div className="flex items-center gap-2">
            <div
              className="w-5 h-2.5 rounded border-2 flex-shrink-0"
              style={{
                borderColor: "#6b7280",
                backgroundColor: "#f8fafc"
              }}
            ></div>
            <span className="text-xs text-gray-600">
                Standard Component
            </span>
          </div>
        </div>
      </div>
      
      
      <div
        ref={containerRef}
        className={`w-full h-full overflow-hidden cursor-grab active:cursor-grabbing ${
          isFullscreen ? "fixed inset-0 z-50" : ""
        }`}
        style={{
          backgroundColor: isDarkMode ? "#282a36" : "#f9fafb",
          // Allow pinch-to-zoom but prevent other browser behaviors
          touchAction: "pan-x pan-y pinch-zoom",
          userSelect: "none",
          WebkitTouchCallout: "none",
          WebkitUserSelect: "none",
          KhtmlUserSelect: "none",
          MozUserSelect: "none",
          msUserSelect: "none"
        }}
        tabIndex={0} // Make the container focusable for keyboard events
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onWheel={handleWheel}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <div
          ref={svgContainerRef}
          className="w-full h-full flex items-center justify-center"
          style={{
            transform: `scale(${zoom}) translate(${position.x / zoom}px, ${position.y / zoom}px)`,
            transformOrigin: "center center",
            transition: isPanning ? "none" : "transform 0.2s ease"
          }}
          dangerouslySetInnerHTML={{ __html: svg }}
        />
      </div>
    </div>
  );
};

export default MermaidViewer;